import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useNotification } from 'config/Notification';

import{
  Button,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  Collapse,
  Divider,
  FormControlLabel,
  IconButton,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  TextField
} from '@mui/material';
import CreditScoreIcon from '@mui/icons-material/CreditScore';
import CheckIcon from '@mui/icons-material/Check';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

import { 
  fetchReportsByID as fetchReportsByIDService
} from 'services/modules/rhymscan/report';
import { 
  fetchReviewReports as fetchReviewReportsService,
  fetchAlertsByQuery as fetchAlertsByQueryService,
  saveAlerts as saveAlertsService 
} from 'services/modules/rhymscan/alert';

function ReportValidate() {
  // useParams hook 
  const { id } = useParams();

  // Redux dispatch
  const dispatch = useDispatch();

  // Notification
  const {openNotification}=useNotification();
  
  // Redux selectors
  const reportContainer = useSelector((state) => state.rsReport.singlereport);
  const dimensionsContainer = useSelector((state) => state.rsData.dimensions);
  const singleAlertContainer=useSelector((state)=>state.rsAlert.singlealert);

  // State variables
  const [totalReportsCount, setTotalReportsCount] = useState(0);
  const [checked, setChecked] = useState(false);
  const [editModes, setEditModes] = useState([]);
  const [editedResults, setEditedResults] = useState([]);
  const [isDisabled, setIsDisabled] = useState(true);
  const [mainDimensions, setMainDimensions] = useState([]);
  const [selectedDimension, setSelectedDimension] = useState(null);
  const [selectedSubDimension, setSelectedSubDimension] = useState(null);
  
  const params = { dimension: selectedSubDimension, report: id, limit: 50 };

  useEffect(() => {
    if (dimensionsContainer && dimensionsContainer.length > 0) {
      let activeDimensions = dimensionsContainer?.filter(dim=>dim.status===true && dim.subDimensions);
      const filtereddimensions = activeDimensions?.map(dim => dim.subDimensions).flat().filter(subDim => subDim.status === true);
      setTotalReportsCount(filtereddimensions?.length);
    }
  }, [dimensionsContainer]);

  const progress = totalReportsCount!==0?Math.round(((reportContainer?.progress ?? []).filter((prog) => prog.reviewed).length/totalReportsCount) * 100):0;

  // useEffect hook sets the edit modes and edited results equal to length of alert results
  useEffect(() => {
    if (singleAlertContainer && singleAlertContainer.results) {
      setEditModes(Array(singleAlertContainer.results.length).fill(false));
      setEditedResults(Array(singleAlertContainer.results.length).fill({}));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // useEffect hook fetches report by ID and filters main dimensions
  useEffect(() => {
    dispatch(fetchReportsByIDService({ id }));
    const filteredMainDimensions = dimensionsContainer.filter((dim) => !!dim.subDimensions && dim.status === true);
    setMainDimensions(filteredMainDimensions);
    const firstDimensionWithoutSubDimensions = dimensionsContainer.find(
      (dim) => !dim.subDimensions
    );
    if (firstDimensionWithoutSubDimensions) {
      setSelectedDimension(firstDimensionWithoutSubDimensions.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);
  
  // useEffect hook disables the review option when clicked on dimensions and already reviewed subdimensions
  useEffect(() => {
    if (reportContainer && reportContainer.progress) {
      const defaultValue =
        reportContainer &&
        reportContainer.progress &&
        !!reportContainer.progress.find(
          (prog) =>
            prog.reviewed && prog.dimension.id === selectedSubDimension
        );

      setChecked(defaultValue);

      setIsDisabled(
        !reportContainer ||
        !!reportContainer.progress.find(
          (prog) =>
            prog.reviewed && prog.dimension.id === selectedSubDimension
        )
      );
    }
  }, [reportContainer, selectedSubDimension]);
  
  // useEffect hook handles checkbox changes
  useEffect(() => {
    const handleCheckboxChange = async () => {
      if (!isDisabled && checked) {
        dispatch(
          fetchReviewReportsService({
            data: {
              report: id,
              dimension: selectedSubDimension,
            },
          })
        );
      }
    };

    handleCheckboxChange();
    dispatch(fetchReportsByIDService({ id }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDisabled, checked,  selectedSubDimension]);

  // useEffect hook fetches alerts when clicked on a particular subdimension,by default it is set to first subdimension from the list
  useEffect(()=>{
    if(selectedSubDimension && selectedDimension){
      const params={dimension:selectedSubDimension,report:id,limit:50}
      dispatch(fetchAlertsByQueryService({params}))
    }else {
      if (dimensionsContainer&&dimensionsContainer.length > 0) {
        const firstDimensionId = dimensionsContainer[0].id;
        const params = { dimension: firstDimensionId, report: id, limit: 50 };
        dispatch(fetchAlertsByQueryService({ params }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[selectedSubDimension,selectedDimension])

  // Handles changes in alert fields
  const handleInputChange = (e, index, key) => {
    const { value } = e.target;
    setEditedResults((prevResults) => {
      const updatedResults = [...prevResults];
      if (key.startsWith('data.')) {
        const dataIndex = index;
        const dataKey = key.substring(5);
        updatedResults[dataIndex] = {
          ...(updatedResults[dataIndex] || {}),
          data: {
            ...(updatedResults[dataIndex]?.data || {}),
            [dataKey]: value
          }
        };
      } else {
        updatedResults[index] = {
          ...(updatedResults[index] || {}),
          [key]: value
        };
      }
      return updatedResults;
    });
  };

  // Handles editing the fields for alerts and also sets edit modes
  const handleEdit = (index) => {
    setEditedResults((prevResults) => {
      const updatedResults = [...prevResults];
      updatedResults[index] = { ...(singleAlertContainer.results[index] || {}) };
      return updatedResults;
    });
    setEditModes((prevModes) => {
      const updatedModes = [...prevModes];
      updatedModes[index] = true;
      return updatedModes;
    });
  };

  // Handles saving the edited alert fields
  const handleSave = async (index) => {
    const editedData = editedResults[index];
    const originalData = singleAlertContainer.results[index];
    const {title, impact, remediation, evidence, time,id} = originalData;
  
    const payload = {
      score: editedData?.score !== undefined && editedData.score !== null && editedData.score !== '' 
      ? editedData.score 
      : 0, // Default to zero if not set or empty
      title: editedData?.title !== undefined ? editedData.title : title,
      impact: editedData?.impact !== undefined ? editedData.impact : impact,
      remediation: editedData?.remediation !== undefined ? editedData.remediation : remediation,
      evidence: editedData?.evidence !== undefined ? editedData.evidence : evidence,
      time,
      data: editedData?.data !== undefined ? editedData.data : {},
    };

    try
    {
      const response=await dispatch(saveAlertsService({ id, data: payload }));
      if(response?.payload===200)
        openNotification("Changes Saved!",'success')
      else
        openNotification(response?.payload,'error')     
      await dispatch(fetchAlertsByQueryService({ params }));

      setEditModes((prevModes) => {
        const updatedModes = [...prevModes];
        updatedModes[index] = false;
        return updatedModes;
      });
    }catch(error){
      openNotification('Error while editing the alert', 'error');
    }
  };

  // Returns dimension name from available main dimensions
  const getDimensionName = (mainDimensions, id) => {
    const dimension = mainDimensions.find((d) =>
      d.subDimensions.find((sub) => sub.id === id)
    );
    return dimension ? dimension.label : '';
  };

  // Handles main dimension click
  const handleMainDimensionClick = (dimensionId) => {
    if (selectedDimension === dimensionId) {
      setSelectedDimension(null);
    } else {
      setSelectedDimension(dimensionId);
    }
  };

  // Displays a pop when user clicks on edit for one alert corresponding to a subdimension and clicks on other subdimension without saving the changes
  const handleSelect = async (subDimensionId) => {
    if (editModes.some((mode, index) => mode)) {
      const confirmLeave = window.confirm(
        'Changes are unsaved! Are you sure you want to continue?'
      );
      
      if (!confirmLeave) {
        return;
      }
      else{
        setEditModes((prevModes) => prevModes.map(() => false));
      }
    }

    setSelectedSubDimension(subDimensionId);

    if (selectedDimension && subDimensionId) {
      const params = { dimension: subDimensionId, report: id, limit: 50 };
      await dispatch(fetchAlertsByQueryService({ params }));
    }
  };

  // Returns true if the given subdimension is reviewed
  const getSubDimensionReviewed = (subDimId) => {
    if (reportContainer && reportContainer.progress)
      return (
        reportContainer?.progress.find(
          (prog) => prog.dimension.id === subDimId && prog.reviewed
        ) !== undefined
      );
  };
  
  // Returns true if the given dimension is reviewed
  const getDimensionReviewed = (dim) => {
    if (reportContainer && reportContainer.progress) {
      // Filter sub-dimensions where status is true
      return dim.subDimensions.filter(sub => sub.status === true).every((sub) =>
        reportContainer.progress.some(
          (prog) => prog.dimension.id === sub.id && prog.reviewed 
        )
      );
    }
    return false;
  };
  
  return (
    <div className="main-cont">
      <Card className="card">
        <CardHeader
          title={
            <React.Fragment>
              <div className="card-head">
                <div className="flex-head">
                  <label className="label">
                    <CreditScoreIcon
                      className="upload-icon"
                    />
                    Validation screen
                  </label>
                </div>
                <div>
                  <Button
                    variant="contained"
                    className="back font"
                    onClick={() => window.history.back()}
                  >
                    Back
                  </Button>
                </div>
              </div>
            </React.Fragment>
          }
        />
        <Divider className="divider" />
        <CardContent>
          <div className="row">
            <div className="col-lg-4 col-md-4 col-sm-12 col-xs-12">
              <Card>
                <List component="nav" >
                  {mainDimensions.map((dimension) => (
                    <React.Fragment key={dimension.id}>
                      <ListItem
                        onClick={() => handleMainDimensionClick(dimension.id)}
                      >
                        <ListItemIcon>
                          {getDimensionReviewed(dimension) && (
                            <CheckIcon className="success"/>
                          )}
                        </ListItemIcon>
                        <ListItemText primary={dimension.label} className="name-table" />
                        <ListItemIcon>
                          <IconButton
                            size="small"
                            edge="end"
                         
                            onClick={(e) => {handleMainDimensionClick(dimension.id)}}
                          >
                            {selectedDimension === dimension.id ? (
                              <ExpandLessIcon />
                            ) : (
                              <ExpandMoreIcon />
                            )}
                          </IconButton>
                        </ListItemIcon>
                      </ListItem>
                      <Collapse
                        in={selectedDimension === dimension.id}
                        timeout="auto"
                        unmountOnExit
                      >
                        <List component="div" disablePadding>
                          {dimension.subDimensions.filter(subDimension => subDimension.status === true).map((subDimension) => (
                            <React.Fragment key={subDimension.id}>
                              <ListItem
                                className={`nested-list-item ${selectedSubDimension === subDimension.id ? 'selected-item' : ''}`}
                                onClick={() => handleSelect(subDimension.id)}
                              >
                                <ListItemIcon>
                                  {getSubDimensionReviewed(subDimension.id) && (
                                    <CheckIcon className="success"/>
                                  )}
                                </ListItemIcon>
                                <ListItemText
                                  primary={subDimension.label}
                                  className="nested-list-text"
                                />
                              </ListItem>
                              <Divider className="divider" />
                            </React.Fragment>
                          ))}               
                        </List>
                      </Collapse>
                      <Divider className="divider" />
                    </React.Fragment>
                  ))}
                </List>
              </Card>
            </div>
            <div className="col-lg-8 col-md-8 col-sm-12 col-xs-12">
              <Card>
                <CardHeader title="Review Progress" />
                <CardContent>
                  <div className="flex">
                    <LinearProgress
                      variant="determinate"
                      value={progress}
                      color="secondary"
                      className="linear-progress"
                    />
                    <h3 className="review-progress">
                      {progress}%
                    </h3>
                  </div>
                  <FormControlLabel
                    control={
                      <Checkbox
                        color="primary"
                        checked={checked|| selectedSubDimension === null}
                        onChange={() => setChecked((prevChecked) => !prevChecked)}
                        disabled={isDisabled|| selectedSubDimension === null}
                      />
                    }
                    label="Reviewed"
                  />
                </CardContent>
              </Card>
              <div className="row main-cont multi-card-container">
                {singleAlertContainer && singleAlertContainer.results&& singleAlertContainer.results.map((result, index) => (
                  <Card key={index} className="col-lg-4 col-md-4 col-xs-12 col-sm-12 multi-card">
                    <CardContent className="card-overflow">
                      <div className='flex justify-between full-width-field'>
                        <div>
                          <p >{getDimensionName(mainDimensions, result.dimension.id)}</p>
                        </div>
                        <div>
                          {editModes[index] ? (
                            <Button variant="contained" onClick={() => handleSave(index)}>
                              Save
                            </Button>
                          ) : (
                            <Button variant="contained" className="edit-alerts" onClick={() => handleEdit(index)}>
                              Edit
                            </Button>
                          )}
                        </div>
                      </div>
                      <div>
                        <h6 className="text-blueviolet">
                          {result.dimension.label}
                        </h6>
                      </div>
                      <br></br>
                      <div className="custom-row">
                        <div className="col-4 word-wrap text-bold">Score</div>
                        <div className="col-8 word-wrap">
                          {editModes[index] ? (
                            <TextField
                              variant="standard"
                              multiline
                              value={editedResults[index]?.score !== undefined ? editedResults[index]?.score : ''}
                              onChange={(e) => handleInputChange(e, index, 'score')}
                            />
                          ) : (
                            result.score
                          )}
                        </div>
                      </div>
                      <div className="custom-row">
                        <div className="col-4 word-wrap text-bold">Title</div>
                        <div className="col-8 word-wrap">
                          {editModes[index] ? (
                            <TextField
                              variant="standard"
                              multiline
                              value={editedResults[index]?.title || ''}
                              onChange={(e) => handleInputChange(e, index, 'title')}
                            />
                          ) : (
                            result.title
                          )}
                        </div>
                      </div>
                      <div className="custom-row">
                        <div className="col-4 word-wrap text-bold">Impact</div>
                        <div className="col-8 word-wrap">
                          {editModes[index] ? (
                            <TextField
                              variant="standard"
                              multiline
                              value={editedResults[index]?.impact || ''}
                              onChange={(e) => handleInputChange(e, index, 'impact')}
                            />
                          ) : (
                            result.impact
                          )}
                        </div>
                      </div>
                      <div className="custom-row">
                        <div className="col-4 word-wrap text-bold">Remediation</div>
                        <div className="col-8 word-wrap">
                          {editModes[index] ? (
                            <TextField
                              variant="standard"
                              multiline
                              value={editedResults[index]?.remediation || ''}
                              onChange={(e) => handleInputChange(e, index, 'remediation')}
                            />
                          ) : (
                            result.remediation
                          )}
                        </div>
                      </div>
                      <div className="custom-row">
                        <div className="col-4 word-wrap text-bold">Evidence</div>
                        <div className="col-8 word-wrap">
                          {editModes[index] ? (
                            <TextField
                              variant="standard"
                              multiline
                              value={editedResults[index]?.evidence || ''}
                              onChange={(e) => handleInputChange(e, index, 'evidence')}
                            />
                          ) : (
                            result.evidence
                          )}
                        </div>
                      </div>
                      {result.data && ( 
                        <>
                          {Object.keys(result.data).map((key, dataIndex) => (
                            <div key={dataIndex} className="custom-row">
                              <div className="col-4 word-wrap text-bold">
                                {
                                  key
                                    .replace(/_/g, ' ') // Replace underscore with space
                                    .replace(/(?:^|\s)\S/g, (char) => char.toUpperCase()) // Capitalize first letter of each word
                                    .replace('ThreatActorDescription', 'ThreatActor Description') // Revert special case formatting
                                }
                              </div>
                              <div className="col-8 word-wrap">
                                {editModes[index] ? (
                                  <TextField
                                    variant="standard"
                                    multiline
                                    value={editedResults[index]?.data?.[key] || ''}
                                    onChange={(e) => handleInputChange(e, index, `data.${key}`)}
                                  />
                                ) : (
                                  result.data[key]
                                )}
                              </div>
                            </div>
                          ))}
                        </>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>        
        </CardContent>
      </Card>
    </div>
  );
}

export default ReportValidate;